import React, { useState, useEffect } from "react";
import {
  Badge,
  IconButton,
  Tooltip,
  Box,
  Popover,
  Tabs,
  Tab,
  Divider,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  AlarmOnOutlined,
  Circle,
  Done,
  MoreHoriz,
  NotificationsOutlined,
  Visibility,
} from "@mui/icons-material";
import { useAuth } from "hooks";
import { useNavigate } from "react-router-dom";

import { getSocketInstance } from "socket/socket.io";

import {
  useGetShipmentActionNotificationListQuery,
  useUpdateShipmentActionMutation,
  useReadAllShipmentActionsMutation,
} from "store/services/shipmentActionApi";

import { toast } from "react-toastify";
import notificationSound from "assets/audio/bell.wav";

const ShipmentActionNotifications = () => {
  const { loggedInUser, isVendor } = useAuth();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [dotAnchorEl, setDotAnchorEl] = useState(null);
  const [isTab, setIsTab] = useState("All");
  const [isUpdating, setIsUpdating] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const {
    data: shipmentActionsData,
    isLoading: loadingNotification,
    isFetching: fetchingNotification,
    refetch: fetchShipmentAction,
  } = useGetShipmentActionNotificationListQuery({
    userId: loggedInUser?._id,
    isVendor,
    vendorId: loggedInUser._id,
    status: isTab === "unread" ? "Open" : undefined,
  });

  useEffect(() => {
    if (shipmentActionsData?.data) {
      setNotifications(shipmentActionsData.data);
    }
  }, [shipmentActionsData]);

  const [updateShipmentAction] = useUpdateShipmentActionMutation();
  const [readAllShipmentActions] = useReadAllShipmentActionsMutation();

  const isBusy = loadingNotification || fetchingNotification || isUpdating;

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;

  const dotOpen = Boolean(dotAnchorEl);
  const dotId = dotOpen ? "dot-popover" : undefined;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleDotClick = (event) => setDotAnchorEl(event.currentTarget);
  const handleDotClose = () => setDotAnchorEl(null);

  const handleChange = (event, newValue) => setIsTab(newValue);

  const onReadAllNotifications = async () => {
    try {
      await readAllShipmentActions().unwrap();
      // Optimistically mark all local notifications as Read
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          sourceRef: { ...n.sourceRef, status: "Read" },
          contextData: { ...n.contextData, actionStatus: "Read" },
        }))
      );
      toast.success("All actions marked as read");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to mark all as read");
    } finally {
      handleDotClose();
    }
  };

  const onViewAllNotifications = () => {
    handleDotClose();
    handleClose();
    if (isVendor) {
      navigate("/app/vendor/action-list/shipment-action/?tab=All");
    }
    else {
      navigate("/app/crm/action-list/shipment-action/?tab=All");
    }
  };

  useEffect(() => {
    if (!loggedInUser?._id) return;
    fetchShipmentAction({
      userId: loggedInUser._id,
      vendorId: loggedInUser._id,
      isVendor,
      ...(isTab === "unread" && { status: "Open" }),
    });
  }, [fetchShipmentAction, loggedInUser?._id, isVendor, isTab]);

  const playNotificationSound = () => {
    const audio = new Audio(notificationSound);
    audio.play().catch((error) => console.error("Audio play failed", error));
  };

  useEffect(() => {
    const socket = getSocketInstance();
    if (!socket || !loggedInUser?._id) return;

    const eventName = `shipment-action:user:${loggedInUser._id}`;

    const handler = (notification) => {
      const isAlreadyInList = notifications.some((n) => n._id === notification?._id);
      if (!isAlreadyInList) {
        playNotificationSound();
        setNotifications((prev) => [notification, ...prev]);
        toast.info(notification.message || notification.title, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      fetchShipmentAction();
    };

    socket.on(eventName, handler);
    return () => socket.off(eventName, handler);
  }, [fetchShipmentAction, loggedInUser?._id, notifications]);

  const handleNotificationClick = async (item) => {
    if (!item?.sourceRef?._id || !item?.sourceRef?.shipmentId) {
      toast.error("Cannot navigate: Shipment action data is missing");
      handleClose();
      return;
    }

    try {
      setIsUpdating(true);

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === item._id
            ? {
              ...n,
              sourceRef: { ...n.sourceRef, status: "Read" },
              contextData: { ...n.contextData, actionStatus: "Read" },
            }
            : n
        )
      );

      await updateShipmentAction({
        id: item.sourceRef._id,
        status: "Read",
        notificationId: item?._id,
        ...(!isVendor && { responseRead: true }),
      }).unwrap();

      const basePath = isVendor ? "/app/vendor/shipment" : "/app/crm/shipment";
      navigate(
        `${basePath}/${item.sourceRef.shipmentId}/detail?tab=action&actionId=${item.sourceRef._id}`
      );
      handleClose();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update notification");
      if (shipmentActionsData?.data) setNotifications(shipmentActionsData.data);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!loggedInUser) return null;

  return (
    <>
      <Tooltip title="Shipment actions" arrow disableHoverListener={open}>
        <IconButton onClick={handleOpen} disabled={isBusy}>
          {shipmentActionsData?.totalItems > 0 ? (
            <Badge
              badgeContent={
                !loadingNotification ? (
                  <span className="relative flex h-5 w-5">
                    <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                      {shipmentActionsData?.totalItems}
                    </span>
                  </span>
                ) : null
              }
              variant="standard"
            >
              <AlarmOnOutlined color="error" />
            </Badge>
          ) : (
            <AlarmOnOutlined />
          )}
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          className="flex min-w-[300px] max-w-[400px] flex-col rounded-md pt-1 md:min-w-[400px]"
          sx={{ height: "600px", overflow: "hidden" }}
        >
          {/* Header */}
          <Box className="flex items-center justify-between px-3">
            <p className="text-[16px] font-bold text-slate-600 dark:text-slate-200">
              My Shipment Actions
            </p>
            {/* 👇 new dot button */}
            <IconButton
              aria-describedby={dotId}
              onClick={handleDotClick}
            >
              <MoreHoriz />
            </IconButton>
          </Box>

          <Box
            className="sticky top-0 z-10"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tabs value={isTab} onChange={handleChange}>
              <Tab label="All" value="All" />
              <Tab label="Unread" value="unread" />
            </Tabs>
          </Box>

          <Divider />

          <Box className="flex h-[500px] flex-col overflow-y-scroll">
            {!isBusy ? (
              notifications.length === 0 ? (
                <p className="mt-5 text-center">No Actions Assigned To You</p>
              ) : (
                notifications.map((item) => (
                  <ListItem
                    key={item?._id}
                    button
                    onClick={() => handleNotificationClick(item)}
                    disabled={isUpdating || !item?.sourceRef?._id}
                  >
                    <Box
                      sx={{ minWidth: 40, minHeight: 40, display: "flex", justifyContent: "center", alignItems: "center" }}
                      className="rounded-full bg-blue-50"
                    >
                      <NotificationsOutlined color="primary" fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={
                        item?.sourceRef?.actionType
                          ? `${item.sourceRef.actionType} - ${item.contextData.actionStatus}`
                          : "Action Unavailable"
                      }
                      secondary={
                        item?.sourceRef
                          ? item.sourceRef.responses?.length
                            ? `New response for shipment ${item.contextData.shipmentId}: ${item.sourceRef.responses[0].text}`
                            : item.message
                          : "Associated action data is missing"
                      }
                      secondaryTypographyProps={{
                        noWrap: true,
                        title: item?.sourceRef
                          ? item.sourceRef.responses?.length
                            ? `New response for shipment ${item.contextData.shipmentId}: ${item.sourceRef.responses[0].text}`
                            : item.message
                          : "Associated action data is missing",
                      }}
                    />
                    {!item.readBy.some(
                      (r) => r?.toString() === loggedInUser?._id?.toString()
                    ) && (
                        <Circle className="ml-auto text-blue-500" sx={{ fontSize: "13px" }} />
                      )}
                  </ListItem>
                ))
              )
            ) : (
              [...Array(6)].map((_, index) => (
                <Box className="mx-3 flex items-center gap-1 py-2" key={index}>
                  <Skeleton variant="circular" width={40} height={40} animation="wave" />
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} className="w-full" animation="wave" />
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Popover>

      {/* 👇 new dot popover */}
      <Popover
        id={dotId}
        open={dotOpen}
        anchorEl={dotAnchorEl}
        onClose={handleDotClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <List disablePadding>
          <ListItem
            className="cursor-pointer p-2 hover:bg-blue-100 dark:hover:bg-slate-600"
            onClick={onReadAllNotifications}
          >
            <ListItemIcon>
              <Done />
            </ListItemIcon>
            <ListItemText primary="Read all Message" />
          </ListItem>

          <ListItem
            className="cursor-pointer p-2 hover:bg-blue-100 dark:hover:bg-slate-600"
            onClick={onViewAllNotifications}
          >
            <ListItemIcon>
              <Visibility />
            </ListItemIcon>
            <ListItemText primary="View all Message" />
          </ListItem>
        </List>
      </Popover>
    </>
  );
};

export default ShipmentActionNotifications;